import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Package, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Plus,
  LogOut,
  User,
  Loader2,
  ShoppingCart,
  FileText,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/giris");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Siparişler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Çıkış yapıldı");
    navigate("/");
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: { label: "Beklemede", color: "#f97316", icon: Clock },
      confirmed: { label: "Onaylandı", color: "#0ea5e9", icon: CheckCircle2 },
      in_progress: { label: "Devam Ediyor", color: "#a855f7", icon: Package },
      review: { label: "İncelemede", color: "#fbbf24", icon: FileText },
      completed: { label: "Tamamlandı", color: "#00d4aa", icon: CheckCircle2 },
      cancelled: { label: "İptal Edildi", color: "#ef4444", icon: AlertCircle }
    };
    return statuses[status] || statuses.pending;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      mobile_app: "Mobil Uygulama",
      web_app: "Web Uygulama",
      automation: "Otomasyon",
      ai_solution: "Yapay Zeka"
    };
    return labels[category] || category;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const stats = {
    total: orders.length,
    active: orders.filter(o => ['pending', 'confirmed', 'in_progress', 'review'].includes(o.status)).length,
    completed: orders.filter(o => o.status === 'completed').length
  };

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0a0a0f]" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#f0f0f5]">
              Hoş Geldin, {user.name.split(' ')[0]}!
            </h1>
            <p className="text-[#9ca3af] mt-1">Siparişlerinizi buradan takip edebilirsiniz</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/urunler">
              <Button className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Sipariş
              </Button>
            </Link>
            <Button variant="outline" onClick={handleLogout} className="border-[#2a2a3a] text-[#9ca3af]">
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#00d4aa]/10 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-[#00d4aa]" />
              </div>
              <div>
                <p className="text-[#9ca3af] text-sm">Toplam Sipariş</p>
                <p className="text-2xl font-bold text-[#f0f0f5]">{stats.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#0ea5e9]/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#0ea5e9]" />
              </div>
              <div>
                <p className="text-[#9ca3af] text-sm">Aktif Projeler</p>
                <p className="text-2xl font-bold text-[#f0f0f5]">{stats.active}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#a855f7]/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#a855f7]" />
              </div>
              <div>
                <p className="text-[#9ca3af] text-sm">Tamamlanan</p>
                <p className="text-2xl font-bold text-[#f0f0f5]">{stats.completed}</p>
              </div>
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00d4aa] to-[#0ea5e9] flex items-center justify-center">
              <User className="w-7 h-7 text-[#0a0a0f]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[#f0f0f5]">{user.name}</h3>
              <p className="text-[#9ca3af] text-sm">{user.email}</p>
            </div>
            <div className="text-right hidden md:block">
              {user.company && <p className="text-[#f0f0f5]">{user.company}</p>}
              {user.phone && <p className="text-[#9ca3af] text-sm">{user.phone}</p>}
            </div>
          </div>
        </div>

        {/* Orders */}
        <div>
          <h2 className="text-xl font-bold text-[#f0f0f5] mb-4">Siparişlerim</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20 bg-[#16161f] border border-[#2a2a3a] rounded-xl">
              <ShoppingCart className="w-16 h-16 text-[#9ca3af] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#f0f0f5] mb-2">
                Henüz Siparişiniz Yok
              </h3>
              <p className="text-[#9ca3af] mb-6">
                Ürünlerimizi inceleyerek ilk siparişinizi oluşturun.
              </p>
              <Link to="/urunler">
                <Button className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]">
                  Ürünleri İncele
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const status = getStatusInfo(order.status);
                const StatusIcon = status.icon;
                
                return (
                  <div
                    key={order.id}
                    className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6 hover:border-[#00d4aa]/50 transition-colors"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-[#f0f0f5]">
                            {order.product_name}
                          </h3>
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                            style={{ 
                              backgroundColor: `${status.color}20`,
                              color: status.color
                            }}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#9ca3af]">
                          <span className="px-2 py-1 bg-[#0a0a0f] rounded">
                            {getCategoryLabel(order.project_type)}
                          </span>
                          <span className="text-[#00d4aa] font-semibold">
                            {order.price}
                          </span>
                          <span>
                            {formatDate(order.created_at)}
                          </span>
                        </div>

                        {order.description && (
                          <p className="text-[#9ca3af] text-sm mt-3 line-clamp-2">
                            {order.description}
                          </p>
                        )}

                        {order.admin_notes && (
                          <div className="mt-3 p-3 bg-[#0a0a0f] rounded-lg border-l-2 border-[#00d4aa]">
                            <p className="text-[#9ca3af] text-sm">
                              <span className="text-[#00d4aa] font-medium">Not: </span>
                              {order.admin_notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
